import flags from '../../common/flag'

/**
 * Format position value for display
 * @param {number|null|undefined} pos - The exact position value (current_rank/
 *   last_position from the backend), or null/undefined when the game wasn't
 *   found in that period's rankings at all.
 * @param {number} [total] - Total number of apps actually ranked for this
 *   category. Used only as the ">N" fallback when pos itself is unavailable —
 *   not ranked at all is worse than every one of the total ranked apps.
 * @returns {string} Formatted position string
 */
export const formatPosition = (pos, total) => {
  if (pos === undefined || pos === null) {
    return typeof total === 'number' && total > 0 ? `>${total}` : "N/A"
  }
  return pos.toString()
}

/**
 * Check if ranking is active
 * @param {string} currentPosition - Formatted current position
 * @returns {boolean} Whether ranking is active
 */
export const isRankingActive = (currentPosition) => {
  return currentPosition !== "N/A" && !isNaN(currentPosition)
}

/**
 * Generate random percentage for demo purposes
 * @returns {number} Random number between 1-100
 */
export const generateRandomPercentage = () => {
  return Math.floor(Math.random() * 100) + 1
}


/**
 * Get auth headers for API requests
 * @returns {Object} Headers object with authorization
 */
export const getAuthHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  Accept: "application/json, text/plain, */*",
})

/**
 * Get content type headers for POST requests
 * @returns {Object} Headers object with content type and authorization
 */
export const getPostHeaders = () => ({
  ...getAuthHeaders(),
  "Content-Type": "application/json",
})

/**
 * Create button style with hover state
 * @param {boolean} isHovered - Whether button is hovered
 * @param {Object} baseStyle - Base button style
 * @param {string} primaryColor - Primary color
 * @param {string} hoverColor - Hover color
 * @returns {Object} Complete button style
 */
export const createButtonStyle = (isHovered, baseStyle, primaryColor, hoverColor) => ({
  ...baseStyle,
  background: isHovered ? hoverColor : primaryColor,
})

/**
 * Map of common country names to ISO country codes
 * Used for displaying country flags
 */
export const countryCodeMap = {
  "United States": "US",
  "United Kingdom": "GB",
  "France": "FR",
  "Germany": "DE",
  "India": "IN",
  "Australia": "AU",
  "Austria": "AT",
  "Belgium": "BE",
  "Canada": "CA",
  "Denmark": "DK",
  "Finland": "FI",
  "Iceland": "IS",
  "Ireland": "IE",
  "Italy": "IT",
  "Japan": "JP",
  "Mexico": "MX",
  "Netherlands": "NL",
  "New Zealand": "NZ",
  "Norway": "NO",
  "Poland": "PL",
  "Singapore": "SG",
  "South Korea": "KR",
  "Spain": "ES",
  "Sweden": "SE",
  "Switzerland": "CH",
  "Taiwan": "TW"
}

/**
 * Get country code from country name
 * @param {string} countryName - Full country name
 * @returns {string|null} ISO country code or null if not found
 */
export const getCountryCode = (countryName) => {
  return countryCodeMap[countryName] || null;
}

/**
 * Map of ISO country codes to common country names
 * Used for converting country codes back to full names
 */
export const countryNameMap = {
  "US": "United States",
  "GB": "United Kingdom", 
  "FR": "France",
  "DE": "Germany",
  "IN": "India",
  "AU": "Australia",
  "AT": "Austria",
  "BE": "Belgium",
  "CA": "Canada",
  "DK": "Denmark",
  "FI": "Finland",
  "IS": "Iceland",
  "IE": "Ireland",
  "IT": "Italy",
  "JP": "Japan",
  "MX": "Mexico",
  "NL": "Netherlands",
  "NZ": "New Zealand", 
  "NO": "Norway",
  "PL": "Poland",
  "SG": "Singapore",
  "KR": "South Korea",
  "ES": "Spain",
  "SE": "Sweden",
  "CH": "Switzerland",
  "TW": "Taiwan"
}

/**
 * Get country name from country code using API countries data
 * @param {string} countryCode - ISO country code
 * @param {Array} countriesData - Countries data from API
 * @returns {string} Full country name or original code if not found
 */
export const getCountryName = (countryCode, countriesData = []) => {
  // First try to find in API data
  const country = countriesData.find(c => c.code === countryCode || c.name === countryCode)
  if (country) {
    return country.name
  }
  
  // Fallback to hardcoded map for backward compatibility
  return countryNameMap[countryCode] || countryCode;
}

/**
 * Get flag component for a country
 * @param {string} countryName - Full country name
 * @returns {React.Component|null} Flag component or null if not found
 */
export const getCountryFlag = (countryName) => {
  const code = getCountryCode(countryName);
  if (!code || !flags[code]) return null;
  
  const FlagComponent = flags[code];
  return FlagComponent;
} 